import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import MembershipActivity from "../../Components/MembersActivity";
import VerticalSpace from "../../Components/VerticalSpace";
import MembershipLessons from "../../Components/LessonsRedeemed";
import Switch from "../../Components/Common/Switch";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import "../../stylesheets/theme.css";
import "../../stylesheets/common.css";
import gsap from "gsap";
import { baseURL, getRequest } from "../../api/apiClient";
import formatDate from "../DateFormat";
import Loader from "../../utils/Loading";
import DatePicker from "react-datepicker";
import Toast from "../Common/Toast";
import StatusChangeConfirmation from "../Common/StatusUpdateConfirmation";

interface Activity {
  id: number;
  title: string;
  progress: string;
  durationInDays: string;
  createdAt: string;
  type: boolean;
  level: string;
}

interface lessons {
  id: number;
  type: string;
  points: string;
  createdAt: string;
  action: boolean;
}

interface Members {
  id?: number;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  memberSince?: string;
  totalPoints?: number;
  level?: number;
  redeemPoints?: number;
  active?: boolean;
}



const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 720px;
`;

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

const StyledInput = styled.input`
  border-radius: 10px;
  border: none;
  padding: 10px;
  height: 40px;
  font-size: 14px;
  width: 100%;
`;

const ActivityScrollBody = styled.div`
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }
`;

const LessonsScrollBody = styled.div`
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }
`;

const EditMemberShipUser = () => {
  const { id } = useParams();
  const [activity, setActivity] = useState<Activity[]>([]);
  const [lessons, setLessons] = useState<lessons[]>([]);
  const [members, setMembers] = useState<Members>();
  const [memberSince, setMemberSince] = useState<Date | null>(new Date());
  const [showGeneralForm, setShowGeneralForm] = useState(true);
  const [selectedButton, setSelectedButton] = useState("General");
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    setIsLoading(true);
    if (id) {
      const response = await getRequest(`${baseURL}/users/${id}`);
      if (response?.data?.success) {
        const userDataFromApi = response.data.data.user;
        const userActivity = response?.data?.data?.userActivity;
        const userLessonsRedeemed = response?.data?.data?.userLessonsRedeemed;
        userDataFromApi.formattedCreatedAt = new Date(
          userDataFromApi.memberSince
        ).toLocaleDateString("en-GB");
        setMembers(userDataFromApi);
        setMemberSince(userDataFromApi?.createdAt);
        setIsSwitchOn(userDataFromApi.active);
        setActivity(userActivity);
        setLessons(userLessonsRedeemed);
      }
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  const handleButtonClick = (buttonName: string) => {
    setShowGeneralForm(buttonName === "General");
    setSelectedButton(buttonName);
  };

  const toggleSwitch = () => {
    setShowStatusPopup(true);
  };

  const handleUpdateStatus = async () => {
    const response = await getRequest(`${baseURL}/users/change-status/${id}`);
    if (response?.status === 200) {
      Toast(response.data.statusMessage, "success");
      navigate(-1);
    }
  };

  const handleCancelStatusChange = () => {
    setShowStatusPopup(false);
  };

  const handleBackClick = async () => {
    navigate(-1);
  };

  const splitFullName = (fullName: string) => {
    const names = fullName.split(" ");
    const firstName = names[0];
    const lastName = names.length > 1 ? names.slice(1).join(" ") : "";
    return { firstName, lastName };
  };

  useEffect(() => {
    let timeLine = gsap.timeline();

    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
      stagger: 0.3,
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".left-container", {
      opacity: 0,
      x: -40,
      duration: 0.3,
      ease: "power2.out",
      stagger: 0.2,
    });

    timeLine.from(".right-container", {
      opacity: 0,
      x: 40,
      duration: 0.3,
      ease: "power2.out",
      stagger: 0.2,
    });
  }, []);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3 main-page">
              <div
                className="d-flex align-items-center go-back"
                onClick={handleBackClick}
                style={{ width: "fit-content" }}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="me-2"
                  style={{
                    backgroundColor: "#D9D9D9",
                    padding: "7px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: "#000",
                  }}
                />
                <label
                  style={{
                    color: "#FF9E00",
                    fontSize: "16px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  Back
                </label>
              </div>
              <h4
                className="row-container"
                style={{
                  textAlign: "center",
                  margin: "auto",
                  color: "#7542E4",
                  fontWeight: "bold",
                }}
              >
                Memberships premium
              </h4>
              <VerticalSpace height={20} />
              <div className="row p-3 profile-main">
                <div className="col user-profile">
                  <div className="row align-items-center mb-3 left-container">
                    <div className="col-auto">
                      <h3 style={{ fontWeight: "bold" }}>Profile</h3>
                    </div>
                    <Switch isOn={isSwitchOn} onToggle={toggleSwitch} />
                  </div>
                  <div
                    className="container left-container"
                    style={{
                      backgroundColor: "rgb(220 220 220 / 66%)",
                      borderRadius: "30px",
                      padding: "20px",
                    }}
                  >
                    <div className="row p-2">
                      <button
                        type="button"
                        className={`btn ${
                          selectedButton === "General"
                            ? "btn-success"
                            : "btn-outline-dark"
                        } me-2 text-center user`}
                        style={{
                          color:
                            selectedButton === "General" ? "#FFFFFF" : "#100F15",
                          backgroundColor:
                            selectedButton === "General"
                              ? "#000000"
                              : "transparent",
                          border:
                            selectedButton === "General"
                              ? "none"
                              : "1px solid #100F15",
                          borderRadius: "46px",
                          padding: "8px 20px",
                          fontSize: "10px",
                          width: "auto",
                        }}
                        onClick={() => handleButtonClick("General")}
                      >
                        General
                      </button>
                    </div>
                    <VerticalSpace height={14} />
                    {showGeneralForm && (
                      <div className="col">
                        <form className="row g-3">
                          <div className="col-md-6">
                            <Label
                              fontSize="14px"
                              padding="0px 0px 0px 8px"
                              fontWeight={400}
                            >
                              ID
                            </Label>
                            <VerticalSpace height={4} />
                            <input
                              type="text"
                              className="form-control"
                              id="id"
                              value={members?.id || "N/A"}
                              style={{
                                borderRadius: "10px",
                                height: "40px",
                                fontSize: "14px",
                              }}
                              readOnly
                            />
                          </div>
                          <div className="col-md-6">
                            <Label
                              fontSize="14px"
                              padding="0px 0px 0px 8px"
                              fontWeight={400}
                            >
                              Member since
                            </Label>
                            <VerticalSpace height={4} />
                            <DatePicker
                              selected={memberSince}
                              dateFormat="dd/MM/yyyy"
                              minDate={new Date()}
                              customInput={<StyledInput />}
                              readOnly
                            />
                          </div>
                          <div className="col-12">
                            <Label
                              fontSize="14px"
                              padding="0px 0px 0px 8px"
                              fontWeight={400}
                            >
                              Name
                            </Label>
                            <VerticalSpace height={4} />
                            <input
                              type="text"
                              className="form-control"
                              id="inputName"
                              value={
                                splitFullName(members?.fullName || "")
                                  .firstName || ""
                              }
                              placeholder="Name"
                              style={{
                                borderRadius: "10px",
                                height: "40px",
                                fontSize: "14px",
                              }}
                              readOnly
                            />
                          </div>
                          <div className="col-12">
                            <Label
                              fontSize="14px"
                              padding="0px 0px 0px 8px"
                              fontWeight={400}
                            >
                              Last Name
                            </Label>
                            <VerticalSpace height={4} />
                            <input
                              type="text"
                              className="form-control"
                              id="inputLastName"
                              value={
                                splitFullName(members?.fullName || "")
                                  .lastName || ""
                              }
                              placeholder="Last name"
                              style={{
                                borderRadius: "10px",
                                height: "40px",
                                fontSize: "14px",
                              }}
                              readOnly
                            />
                          </div>
                          <div className="col-12">
                            <Label
                              fontSize="14px"
                              padding="0px 0px 0px 8px"
                              fontWeight={400}
                            >
                              Email
                            </Label>
                            <VerticalSpace height={4} />
                            <input
                              type="email"
                              className="form-control"
                              id="inputEmail"
                              value={members?.email}
                              placeholder="antonia@lexik.app"
                              style={{
                                borderRadius: "10px",
                                height: "40px",
                                fontSize: "14px",
                              }}
                              readOnly
                            />
                          </div>
                          <div className="col-md-6">
                            <Label
                              fontSize="14px"
                              padding="0px 0px 0px 8px"
                              fontWeight={400}
                            >
                              Total Points
                            </Label>
                            <VerticalSpace height={4} />
                            <input
                              type="text"
                              className="form-control"
                              id="inputpoints"
                              value={members?.totalPoints || "0"}
                              style={{
                                borderRadius: "10px",
                                height: "40px",
                                fontSize: "14px",
                              }}
                              readOnly
                            />
                          </div>
                          <div className="col-md-6">
                            <Label
                              fontSize="14px"
                              padding="0px 0px 0px 8px"
                              fontWeight={400}
                            >
                              Points redeemed
                            </Label>
                            <VerticalSpace height={4} />
                            <input
                              type="text"
                              className="form-control"
                              id="inputredeemPoints"
                              value={members?.redeemPoints || "0"}
                              style={{
                                color: "#7542E4",
                                borderRadius: "10px",
                                height: "40px",
                                fontSize: "14px",
                              }}
                              readOnly
                            />
                          </div>
                          <div className="d-flex justify-content-between align-items-end">
                            <div>
                              <Label
                                fontSize="14px"
                                padding="0px 0px 0px 8px"
                                fontWeight={400}
                              >
                                Level
                              </Label>
                              <VerticalSpace height={4} />
                              <input
                                type="text"
                                className="form-control"
                                id="inputlevel"
                                value={members?.level || 0}
                                style={{
                                  borderRadius: "10px",
                                  height: "40px",
                                  fontSize: "14px",
                                }}
                                readOnly
                              />
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col user-activity">
                  {/* ── Activity ── */}
                  <h3 className="right-container" style={{ fontWeight: "bold" }}>
                    Activity
                  </h3>
                  <VerticalSpace height={2} />
                  <ScrollableRowContainer>
                    <InnerRowContainer>
                      <Container width="100%" className="right-container">
                        <RowContainer
                          alignItem="center"
                          padding="8px 12px 8px 12px"
                          gap="8px"
                        >
                          <RowContainer flex="6.4">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Name
                            </Label>
                          </RowContainer>
                          <RowContainer flex="4">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Progress
                            </Label>
                          </RowContainer>
                          <RowContainer flex="6">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Start day
                            </Label>
                          </RowContainer>
                          <RowContainer flex="4">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Type
                            </Label>
                          </RowContainer>
                          <RowContainer flex="2">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Level
                            </Label>
                          </RowContainer>
                        </RowContainer>

                        {activity && activity.length > 0 ? (
                          <ActivityScrollBody>
                            <ColumnContainer>
                              {activity.map((act) => (
                                <MembershipActivity
                                  key={act.id}
                                  id={act.id}
                                  name={act.title}
                                  progress={`${act.progress}%`}
                                  startdate={formatDate(act.createdAt)}
                                  type={act.type}
                                  level={act.level}
                                />
                              ))}
                            </ColumnContainer>
                          </ActivityScrollBody>
                        ) : (
                          <>
                            <VerticalSpace height={10} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No records found.
                            </Label>
                          </>
                        )}
                      </Container>
                    </InnerRowContainer>
                  </ScrollableRowContainer>

                  <VerticalSpace height={34} />

                  {/* ── Lessons redeemed ── */}
                  <h3 className="right-container" style={{ fontWeight: "bold" }}>
                    Lessons redeemed
                  </h3>
                  <VerticalSpace height={2} />
                  <ScrollableRowContainer>
                    <InnerRowContainer>
                      <Container width="100%" className="right-container">
                        <RowContainer
                          alignItem="center"
                          padding="8px 12px 8px 12px"
                          gap="8px"
                        >
                          <RowContainer flex="8">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Paquete
                            </Label>
                          </RowContainer>
                          <RowContainer flex="7">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Points
                            </Label>
                          </RowContainer>
                          <RowContainer flex="8">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Date
                            </Label>
                          </RowContainer>
                          <RowContainer flex="5">
                            <Label fontWeight={400} fontSize="14px" color="rgba(164, 163, 167, 1)">
                              Operation
                            </Label>
                          </RowContainer>
                        </RowContainer>

                        {lessons && lessons.length > 0 ? (
                          <LessonsScrollBody>
                            <ColumnContainer>
                              {lessons.map((lesson: any) => (
                                <MembershipLessons
                                  key={lesson.id}
                                  id={lesson.id}
                                  paquete={lesson.type}
                                  level={lesson.points}
                                  date={formatDate(lesson.createdAt)}
                                  operation={lesson.action}
                                />
                              ))}
                            </ColumnContainer>
                          </LessonsScrollBody>
                        ) : (
                          <>
                            <VerticalSpace height={10} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No records found.
                            </Label>
                          </>
                        )}
                      </Container>
                    </InnerRowContainer>
                  </ScrollableRowContainer>

                </div>
              </div>
              <VerticalSpace height={100} />
            </div>
            {showStatusPopup && (
              <StatusChangeConfirmation
                showStatusPopup={showStatusPopup}
                setShowStatusPopup={setShowStatusPopup}
                message={`Are you sure you want to ${
                  isSwitchOn ? "deactivate" : "activate"
                } this user?`}
                statusButtonLabel={isSwitchOn ? "Deactivate" : "Activate"}
                onStatus={handleUpdateStatus}
                onCancel={handleCancelStatusChange}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default EditMemberShipUser;
